import { NO_FOUND_STATE_WORDS, isExpectedTriplet, verdictWords } from '@/lib/expected-noyau-words';
import { BEFORE_NPC_LABEL, replaySeriesLine } from '@/lib/npc-replays-labels';
import {
  ANALYST_LEVELS,
  ANALYST_LEVEL_LABELS,
  NOYAU_CODES,
  type NoyauCode,
  type NoyauVerdict,
  type NpcExpectedNoyau,
  type NpcReplayRow,
} from '@matter_extractor/shared';

/**
 * What the tab « Rejeux » of an NPC and the page of a series say of the expected noyau set against
 * the found one (npc.md §26.4, §26.5, §26.10; issue #216, lot L), PURE.
 *
 * ## Counts of the rows on screen, never a measure
 *
 * A replay row already carries the verdict, the placement and the six notes, computed by the server
 * from the version of the NPC it played (`compareNoyaux`, `placeExpectedNoyau`). Nothing here
 * derives a verdict: it counts the rows it is handed, words the counts, and cuts them where the
 * contract says to.
 *
 * - **One band per experience** (A10): the rows of an experience are counted together, every version
 *   of it, and never summed with those of another experience.
 * - **Cut by expected noyau** (H36): an NPC whose expected noyau changed between its versions has the
 *   line of an experience cut in as many segments as there were expected noyaux.
 * - **A replay under way** (H39) has no determination yet: it is told apart, and counted in none of the
 *   figures. A replay in the bin is not served, so it is not here to be counted.
 */

/* ----------------------------------------------------------------- the words */

/** « 1er », « 2e », « 3e »: the rank as a French ordinal. */
export function rankWord(rank: number): string {
  return rank === 1 ? '1er' : `${rank}e`;
}

/** « 0 point », « 1 point », « 16 points »: the unit agrees from 2 on. */
export function pointsWord(points: number): string {
  return `${points} ${Math.abs(points) > 1 ? 'points' : 'point'}`;
}

/**
 * « v5 », « v1 et v2 », « v1 à v4 », « v1 à v3, v5 »: the version numbers as ranges. A run of two is
 * « v1 et v2 », a run of three or more is « v1 à v3 », and the runs are joined by a comma.
 */
export function versionsLabel(numbers: readonly number[]): string {
  const sorted = [...new Set(numbers)].sort((a, b) => a - b);
  const runs: number[][] = [];
  for (const value of sorted) {
    const last = runs.at(-1);
    if (last !== undefined && last.at(-1) === value - 1) last.push(value);
    else runs.push([value]);
  }
  return runs
    .map((run) => {
      const first = run[0];
      const last = run.at(-1);
      if (first === undefined || last === undefined) return '';
      if (run.length === 1) return `v${first}`;
      return run.length === 2 ? `v${first} et v${last}` : `v${first} à v${last}`;
    })
    .join(', ');
}

/** « 1 rejeu », « 4 rejeux ». */
export function replayCountWord(count: number): string {
  return `${count} ${count > 1 ? 'rejeux' : 'rejeu'}`;
}

function lowerFirst(text: string): string {
  return `${text.charAt(0).toLowerCase()}${text.slice(1)}`;
}

/* ---------------------------------------------------------------- the row */

/** The noyau a replay found: the retained noyau its expected reading carries, else its column Noyau. */
export function foundOf(row: Pick<NpcReplayRow, 'expected' | 'noyau'>): NoyauCode | null {
  return row.expected?.found ?? row.noyau;
}

/** The expected noyau of the version the replay played (or the current one), null without. */
export function expectedOf(row: Pick<NpcReplayRow, 'expected'>): NpcExpectedNoyau | null {
  return row.expected?.expected ?? null;
}

/**
 * A replay still under way that has found nothing yet (H39): it is counted apart, and the tables
 * carry a dash. One that ended without a noyau is NOT waiting: it « n'a pas de trouvé ».
 */
export function isWaiting(row: Pick<NpcReplayRow, 'expected' | 'noyau' | 'status'>): boolean {
  return row.status === 'in-progress' && foundOf(row) === null;
}

/** The tone of the pill of the verdict: a word first, the colour only repeats it. */
export type FaceTone = 'success' | 'warning' | 'accent' | 'danger' | 'neutral' | 'outline';

const VERDICT_TONE: Readonly<Record<NoyauVerdict, FaceTone>> = {
  same: 'success',
  near: 'warning',
  mirror: 'accent',
  none: 'danger',
};

/** What the column « Face à l'attendu » says of a replay. */
export interface FaceToExpected {
  /** `none`: no expected noyau on the sheet; `waiting`: no determination yet; `out-of-base`, `no-found`, `verdict`. */
  readonly kind: 'none' | 'waiting' | 'out-of-base' | 'no-found' | 'verdict';
  /** The pill's words, « - » for `none` and `waiting`, which draw no pill. */
  readonly label: string;
  readonly tone: FaceTone;
  /** What stands under the pill: « attendu actuel », « sans extrait », « en attente de la détermination ». */
  readonly note: string | null;
  readonly verdict: NoyauVerdict | null;
}

/** The words of §26.4: « aucun niveau en commun », « voisin : le Quoi tient », « pas de trouvé », « hors base ». */
export function faceToExpected(
  row: Pick<NpcReplayRow, 'expected' | 'noyau' | 'status'>,
): FaceToExpected {
  const reading = row.expected;
  if (reading === null || reading === undefined) {
    return { kind: 'none', label: '-', tone: 'neutral', note: null, verdict: null };
  }
  const current = reading.provenance.kind === 'current' ? 'attendu actuel' : null;
  if (!isExpectedTriplet(reading.expected)) {
    return {
      kind: 'out-of-base',
      label: 'hors base',
      tone: 'outline',
      note: current,
      verdict: null,
    };
  }
  if (reading.comparison !== null) {
    return {
      kind: 'verdict',
      label: lowerFirst(verdictWords(reading.comparison)),
      tone: VERDICT_TONE[reading.comparison.verdict],
      note: current,
      verdict: reading.comparison.verdict,
    };
  }
  if (isWaiting(row)) {
    return {
      kind: 'waiting',
      label: '-',
      tone: 'neutral',
      note: [current, 'en attente de la détermination'].filter((part) => part !== null).join(' · '),
      verdict: null,
    };
  }
  const why =
    reading.determinationState === null ? null : NO_FOUND_STATE_WORDS[reading.determinationState];
  return {
    kind: 'no-found',
    label: 'pas de trouvé',
    tone: 'neutral',
    note: [current, why].filter((part) => part !== null).join(' · ') || null,
    verdict: null,
  };
}

/* ------------------------------------------------------------ the counts */

/** One found noyau and how many replays found it. */
export interface FoundCount {
  readonly noyau: NoyauCode;
  readonly count: number;
}

/** The found noyaux of the rows, the most found first, the table's order among equals. */
export function foundCounts(rows: readonly NpcReplayRow[]): FoundCount[] {
  const counts = new Map<NoyauCode, number>();
  for (const row of rows) {
    if (isWaiting(row)) continue;
    const found = foundOf(row);
    if (found !== null) counts.set(found, (counts.get(found) ?? 0) + 1);
  }
  return NOYAU_CODES.flatMap((noyau) => {
    const count = counts.get(noyau);
    return count === undefined ? [] : [{ noyau, count }];
  }).sort((a, b) => b.count - a.count);
}

/** The replays that ended with no found noyau (a state of their determination says why, in the grid). */
export function noFoundCount(rows: readonly NpcReplayRow[]): number {
  return rows.filter((row) => !isWaiting(row) && foundOf(row) === null).length;
}

/** The replays under way, which have found nothing yet. */
export function waitingCount(rows: readonly NpcReplayRow[]): number {
  return rows.filter(isWaiting).length;
}

/** How many replays found the very noyau expected: « identique ». Null when the expected noyau is no triplet. */
export function identicalCount(rows: readonly NpcReplayRow[]): number | null {
  const expected = rows.map(expectedOf).find((one) => one !== null) ?? null;
  if (!isExpectedTriplet(expected)) return null;
  return rows.filter((row) => row.expected?.comparison?.verdict === 'same').length;
}

/** « Pourquoi 0/3, Comment 0/3, Quoi 0/3 » over the replays that have a found noyau; null without any. */
export function levelsHeld(rows: readonly NpcReplayRow[]): string | null {
  const compared = rows.flatMap((row) =>
    row.expected?.comparison === null || row.expected?.comparison === undefined
      ? []
      : [row.expected.comparison],
  );
  if (compared.length === 0) return null;
  return ANALYST_LEVELS.map((level) => {
    const held = compared.filter((comparison) => comparison.levels[level]).length;
    return `${ANALYST_LEVEL_LABELS[level]} ${held}/${compared.length}`;
  }).join(', ');
}

function gapRange(values: readonly number[], to: string): string {
  const min = Math.min(...values);
  const max = Math.max(...values);
  return min === max
    ? `à ${pointsWord(min)} ${to}`
    : `de ${min} à ${max} ${Math.abs(max) > 1 ? 'points' : 'point'} ${to}`;
}

/**
 * Where the expected noyau falls, over the replays whose notes could say it (§26.4):
 * « 3e chaque fois, de 16 à 25 points du retenu, jamais aux cartes ». The rank, the gap to the retained
 * noyau (or to the second when the expected noyau IS the retained one) and the cards, each omitted
 * when no row says it (H34); null when no row has a placement.
 */
export function placementSentence(rows: readonly NpcReplayRow[]): string | null {
  const placed = rows.flatMap((row) =>
    row.expected?.placement === null || row.expected?.placement === undefined
      ? []
      : [row.expected.placement],
  );
  if (placed.length === 0) return null;

  const ranks = placed.map((one) => one.rank);
  const minRank = Math.min(...ranks);
  const maxRank = Math.max(...ranks);
  const tied = placed.filter((one) => one.tied).length;
  const allTied = tied === placed.length;
  let where: string;
  if (minRank === maxRank) {
    where = `${rankWord(minRank)}${allTied ? ' ex aequo' : ''}${placed.length > 1 ? ' chaque fois' : ''}`;
    if (!allTied && tied > 0) where += ` (dont ${tied} ex aequo)`;
  } else {
    where = `de ${rankWord(minRank)} à ${rankWord(maxRank)}`;
    if (tied > 0) where += ` (dont ${tied} ex aequo)`;
  }

  const parts = [where];
  const toRetained = placed.filter((one) => one.gapTo === 'retained').map((one) => one.gap);
  const toSecond = placed.filter((one) => one.gapTo === 'second').map((one) => one.gap);
  if (toRetained.length > 0) parts.push(gapRange(toRetained, 'du retenu'));
  if (toSecond.length > 0) parts.push(gapRange(toSecond, 'du second'));

  const onCards = placed.flatMap((one) => (one.onCards === null ? [] : [one.onCards]));
  if (onCards.length > 0) {
    const yes = onCards.filter(Boolean).length;
    if (yes === 0) parts.push(onCards.length > 1 ? 'jamais aux cartes' : 'pas aux cartes');
    else if (yes === onCards.length)
      parts.push(onCards.length > 1 ? 'toujours aux cartes' : 'aux cartes');
    else parts.push(`aux cartes ${yes} fois sur ${onCards.length}`);
  }
  return parts.join(', ');
}

/* -------------------------------------------------------------- the bands */

/** A pill of a band: a found noyau, « pas de trouvé », the expected noyau counted at zero... */
export interface BandChip {
  readonly kind: 'found' | 'no-found' | 'expected' | 'waiting' | 'current';
  readonly noyau: NoyauCode | null;
  readonly count: number;
  /** « PSG × 3 », « pas de trouvé × 1 », « SGP × 0 ». */
  readonly label: string;
  /** The found noyau of this chip IS the expected one: it wears the dashes too. */
  readonly expected: boolean;
}

/** The part of the replays of an experience that were compared to ONE expected noyau (H36). */
export interface BandSegment {
  readonly expected: NpcExpectedNoyau | null;
  /** « attendu SGP, v1 à v4 »: only when the experience has several segments; else null. */
  readonly heading: string | null;
  readonly count: number;
  readonly chips: readonly BandChip[];
  /** « Niveaux tenus : Pourquoi 0/3, Comment 0/3, Quoi 0/3 », null without a found noyau. */
  readonly levels: string | null;
  /** « L’attendu tombe 3e chaque fois, de 16 à 25 points du retenu, jamais aux cartes », null without notes. */
  readonly placement: string | null;
  /** What the segment says when there is nothing to compare: « pas de noyau attendu sur la fiche », « hors base ». */
  readonly remark: string | null;
}

/** One line of the band: an experience, all its versions together, and its segments. */
export interface ExperienceBand {
  readonly experienceId: string;
  readonly experienceName: string;
  /** « 4 rejeux, v24 et v25 ». */
  readonly countLabel: string;
  readonly segments: readonly BandSegment[];
}

function chipsOf(rows: readonly NpcReplayRow[], expected: NpcExpectedNoyau | null): BandChip[] {
  const chips: BandChip[] = foundCounts(rows).map(({ noyau, count }) => ({
    kind: 'found',
    noyau,
    count,
    label: `${noyau} × ${count}`,
    expected: noyau === expected,
  }));
  const none = noFoundCount(rows);
  if (none > 0) {
    chips.push({
      kind: 'no-found',
      noyau: null,
      count: none,
      label: `pas de trouvé × ${none}`,
      expected: false,
    });
  }
  if (isExpectedTriplet(expected) && !chips.some((chip) => chip.noyau === expected)) {
    chips.push({
      kind: 'expected',
      noyau: expected,
      count: 0,
      label: `${expected} × 0`,
      expected: true,
    });
  }
  const waiting = waitingCount(rows);
  if (waiting > 0) {
    chips.push({
      kind: 'waiting',
      noyau: null,
      count: waiting,
      label: `en attente de la détermination × ${waiting}`,
      expected: false,
    });
  }
  const current = rows.filter((row) => row.expected?.provenance.kind === 'current').length;
  if (current > 0) {
    chips.push({
      kind: 'current',
      noyau: null,
      count: current,
      label: current > 1 ? `attendu actuel × ${current}` : 'attendu actuel',
      expected: false,
    });
  }
  return chips;
}

function versionOf(row: NpcReplayRow): number | null {
  return row.expected?.provenance.versionNumber ?? row.npcVersionNumber;
}

function headingOf(expected: NpcExpectedNoyau | null, rows: readonly NpcReplayRow[]): string {
  const versions = versionsLabel(rows.flatMap((row) => versionOf(row) ?? []));
  const lead =
    expected === null
      ? 'pas d’attendu'
      : isExpectedTriplet(expected)
        ? `attendu ${expected}`
        : 'attendu hors base';
  return versions === '' ? lead : `${lead}, ${versions}`;
}

function remarkOf(expected: NpcExpectedNoyau | null): string | null {
  if (expected === null) return 'pas de noyau attendu sur la fiche : rien à comparer';
  return isExpectedTriplet(expected) ? null : 'attendu hors base : rien à comparer';
}

/**
 * The band of the tab « Rejeux »: one line per experience played (A10), in the order the replays
 * come (the most recent first), each cut by expected noyau when the NPC's changed (H36) - the
 * segments from the oldest version of the NPC to the newest.
 */
export function experienceBands(rows: readonly NpcReplayRow[]): ExperienceBand[] {
  const order: string[] = [];
  const byExperience = new Map<string, NpcReplayRow[]>();
  for (const row of rows) {
    const list = byExperience.get(row.experienceId);
    if (list === undefined) {
      order.push(row.experienceId);
      byExperience.set(row.experienceId, [row]);
    } else list.push(row);
  }

  return order.flatMap((experienceId) => {
    const list = byExperience.get(experienceId) ?? [];
    const first = list[0];
    if (first === undefined) return [];

    const byExpected = new Map<string, NpcReplayRow[]>();
    for (const row of list) {
      const key = expectedOf(row) ?? '';
      byExpected.set(key, [...(byExpected.get(key) ?? []), row]);
    }
    const groups = [...byExpected.values()]
      .map((group) => ({
        rows: group,
        oldest: Math.min(...group.map((row) => versionOf(row) ?? Number.POSITIVE_INFINITY)),
      }))
      .sort((a, b) => a.oldest - b.oldest);
    const cut = groups.length > 1;

    const segments: BandSegment[] = groups.map(({ rows: group }) => {
      const expected = expectedOf(group[0] as NpcReplayRow);
      return {
        expected,
        heading: cut ? headingOf(expected, group) : null,
        count: group.length,
        chips: chipsOf(group, expected),
        levels: levelsHeld(group) === null ? null : `Niveaux tenus : ${levelsHeld(group)}`,
        placement: (() => {
          const sentence = placementSentence(group);
          return sentence === null ? null : `L’attendu tombe ${sentence}`;
        })(),
        remark: remarkOf(expected),
      };
    });

    const versions = versionsLabel(list.flatMap((row) => row.experienceVersionNumber ?? []));
    return [
      {
        experienceId,
        experienceName: first.experienceName,
        countLabel:
          versions === ''
            ? replayCountWord(list.length)
            : `${replayCountWord(list.length)}, ${versions}`,
        segments,
      },
    ];
  });
}

/**
 * The reminder above the band: the expected noyau of the CURRENT version of the NPC (« attendu de la
 * v5 SGP Souverain Magicien »). Null expected is « pas de noyau attendu sur la fiche ».
 */
export interface CurrentExpected {
  readonly versionNumber: number;
  readonly expected: NpcExpectedNoyau | null;
}

/* --------------------------------------------------------------- the grid */

/** One case of the grid: a noyau's note for a replay. */
export interface GridCell {
  readonly noyau: NoyauCode;
  readonly score: number | null;
  /** The retained noyau of the replay: ringed in ink and bold. */
  readonly retained: boolean;
  /** The expected noyau of the version the replay played: ringed in dashes. */
  readonly expected: boolean;
  /** The share of bleu nuit mixed into white, 0 to 45, null for an empty case. */
  readonly mix: number | null;
}

/** One line of the grid: a replay, its six notes, its words. */
export interface GridRow {
  readonly sessionId: string;
  readonly startedAt: string;
  /** « Le Train v25 ». */
  readonly experience: string;
  /** « Petit banc #204 · passe 2 », « hors série », « joué avant les NPC ». */
  readonly series: string;
  readonly cells: readonly GridCell[];
  readonly face: FaceToExpected;
}

/**
 * The share of bleu nuit of a case (H40): proportional to the note, and at most 45 % so that the ink
 * of the figure keeps its contrast on the darkest case (AA).
 */
export function noteMix(score: number): number {
  return Math.round(Math.min(100, Math.max(0, score)) * 0.45);
}

/** The fill of a case: a mix of the bleu nuit and the white, in tokens - the value IS the note. */
export function noteBackground(score: number): string {
  return `color-mix(in srgb, var(--color-bleu-nuit) ${noteMix(score)}%, var(--bg-surface))`;
}

function seriesOfRow(row: NpcReplayRow): string {
  const label = row.seriesLabel ?? (row.seriesId === null ? null : 'La série');
  const line = replaySeriesLine(row);
  // A label that already says it (« jouée avant les NPC ») is not followed by the same words.
  const text = [label, line]
    .filter((part): part is string => part !== null && part !== '')
    .join(' · ');
  return text === '' ? BEFORE_NPC_LABEL : text;
}

/** The grid of the six notes: one line per replay, in the order of the table, a column per noyau. */
export function gridRows(rows: readonly NpcReplayRow[]): GridRow[] {
  return rows.map((row) => {
    const scores = row.noyauScores ?? null;
    const retained = foundOf(row);
    const expected = expectedOf(row);
    return {
      sessionId: row.sessionId,
      startedAt: row.startedAt,
      experience:
        row.experienceVersionNumber === null
          ? row.experienceName
          : `${row.experienceName} v${row.experienceVersionNumber}`,
      series: seriesOfRow(row),
      cells: NOYAU_CODES.map((noyau) => {
        const score = scores?.find((entry) => entry.noyau === noyau)?.score ?? null;
        return {
          noyau,
          score,
          retained: retained === noyau,
          expected: expected === noyau,
          mix: score === null ? null : noteMix(score),
        };
      }),
      face: faceToExpected(row),
    };
  });
}
